import { GA4_REFUSALS } from '@/api/ga4'
import {
  classifyProviderDown,
  classifyRateLimited,
  pickWording,
  screenErrors,
  type ProviderDownKind,
  type RateLimitedKind,
  type Wording,
} from '@/features/ga4-shared/errors'

type TrafficKind = ProviderDownKind | RateLimitedKind | 'range_too_long'

/** What reading the table and downloading it share: a range the API refuses, a provider that is down, a request limit. */
const BASE_WORDING: Record<TrafficKind, Wording> = {
  ...pickWording('provider_down', 'rate_limited'),
  range_too_long: 'That date range is too long for Google Analytics traffic. Choose a shorter date range.',
}

/** A traffic screen: the shared handling, plus its own 403 and 404 (the two things the table and the export mean differently). */
function trafficScreen<K extends string>(own: { forbidden: K; notFound: K; wording: Record<K, Wording> }) {
  return screenErrors<TrafficKind | K>({
    status: {
      403: own.forbidden,
      404: own.notFound,
      422: (error) => (GA4_REFUSALS.rangeTooLong.test(error.message) ? 'range_too_long' : null),
    },
    rules: [classifyRateLimited, classifyProviderDown],
    wording: { ...BASE_WORDING, ...own.wording },
  })
}

/**
 * Reading traffic needs no key and imports nothing: a 503 falls through to the
 * retry wording. The page has no property list to reload, so a 404 is only the
 * property filter, which the page does not send.
 */
const readMessage = trafficScreen({
  forbidden: 'no_analytics_access',
  notFound: 'traffic_not_found',
  wording: {
    no_analytics_access: 'Your role cannot see analytics. Ask an Owner or Administrator for access.',
    traffic_not_found: 'Google Analytics traffic could not be found for this account. Reload the page; if it persists, contact TmT support.',
  },
})

/** The CSV route needs `exports.create`, not `analytics.read`: a refusal names that permission, not the table's. */
const exportMessage = trafficScreen({
  forbidden: 'no_export_access',
  notFound: 'export_unavailable',
  wording: {
    no_export_access: 'Your role cannot export analytics. Ask an Owner or Administrator for access.',
    export_unavailable: 'The export could not be prepared just now. Try again in a moment.',
  },
})

export const trafficErrorMessage = (error: unknown, fallback: string): string => readMessage(error, null, fallback)

export const exportErrorMessage = (error: unknown, fallback: string): string => exportMessage(error, null, fallback)

// --- the first-party source ---------------------------------------------------

/**
 * What the first-party routes refuse, in their own words: they name no provider.
 * The range rule is the Ads report's (`covers at most N days`); a 503 whose message
 * says revenue "could not be converted" is the ECB rate the server would not guess.
 */
export const FIRST_PARTY_REFUSALS = { rangeTooLong: GA4_REFUSALS.rangeTooLong, fxUnavailable: /could not be converted/i } as const

/**
 * The first-party read models' own refusals, in one wording wherever they are said (this
 * page and the competition page): a range over the API's 400 days, and a foreign-currency
 * day with no recorded rate.
 */
export const FIRST_PARTY_WORDING = {
  range_too_long: 'That date range is too long. Choose a range of at most 400 days.',
  fx_unavailable:
    "Some orders are in another currency, and the European Central Bank's rate for one of their days is not available to convert them. Try again later, or choose a more recent date range.",
} as const

/** A first-party screen: only its 403 differs between the table and the export. */
function firstPartyScreen(forbidden: string) {
  return screenErrors<'forbidden' | 'range_too_long' | 'fx_unavailable' | RateLimitedKind>({
    status: {
      403: 'forbidden',
      422: (error) => (FIRST_PARTY_REFUSALS.rangeTooLong.test(error.message) ? 'range_too_long' : null),
      503: (error) => (FIRST_PARTY_REFUSALS.fxUnavailable.test(error.message) ? 'fx_unavailable' : null),
    },
    rules: [classifyRateLimited],
    wording: {
      forbidden,
      ...FIRST_PARTY_WORDING,
      ...pickWording('rate_limited'),
    },
  })
}

const firstPartyRead = firstPartyScreen('Your role cannot see analytics. Ask an Owner or Administrator for access.')

const firstPartyExport = firstPartyScreen('Your role cannot export analytics. Ask an Owner or Administrator for access.')

export const firstPartyErrorMessage = (error: unknown, fallback: string): string => firstPartyRead(error, null, fallback)

export const firstPartyExportErrorMessage = (error: unknown, fallback: string): string =>
  firstPartyExport(error, null, fallback)
